import React from 'react';
import styled from 'styled-components/macro';
import { Input, Button, Typography } from '@deeptrue-frontend/ui';

import {
  Formik,
  Field as FormikField,
  ErrorMessage,
  FormikHelpers,
} from 'formik';
import * as Yup from 'yup';

import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';

interface FormValues {
  new_password: string;
  new_password_confirm: string;
  non_field_errors?: string;
  reset_key?: string;
  organization_name?: string;
  detail?: string;
}

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    new_password: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    new_password_confirm: Yup.string()
      .required(t('errors.fields.required', 'Field is required'))
      .oneOf(
        [Yup.ref('new_password'), null],
        t('errors.fields.match', 'Passwords must match')
      ),
  });

const StyledForm = styled.form`
  width: 100%;
  margin-top: 1rem;
`;

export type SubmitHandler = (
  values: FormValues,
  formHelpers: FormikHelpers<FormValues>
) => void;

interface Props {
  onSubmit: SubmitHandler;
  initialValues?: FormValues;
}

const RecoverPassword = ({
  onSubmit,
  initialValues = {
    new_password: '',
    new_password_confirm: '',
    reset_key: '',
    non_field_errors: '',
    organization_name: '',
    detail: '',
  },
}: Props) => {
  const t = useFormatMessage();
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={getValidationSchema(t)}
    >
      {({ handleSubmit, errors, touched, isSubmitting }) => (
        <StyledForm onSubmit={handleSubmit}>
          <FormikField
            id="new_password"
            name="new_password"
            type="password"
            hasError={touched.new_password && errors.new_password}
            helperText={touched.new_password && errors.new_password}
            as={Input}
            label={t('forms.password.new', 'New password')}
          />
          <FormikField
            id="new_password_confirm"
            name="new_password_confirm"
            type="password"
            hasError={
              touched.new_password_confirm && errors.new_password_confirm
            }
            helperText={
              touched.new_password_confirm && errors.new_password_confirm
            }
            as={Input}
            label={t(
              'forms.password.confirmnewpassword',
              'Confirm new password'
            )}
          />
          <ErrorMessage
            name="reset_key"
            render={(msg) => (
              <Typography variant="copy3" color="punch">
                Key - {msg}
              </Typography>
            )}
          />
          <ErrorMessage
            name="detail"
            render={(msg) => (
              <Typography variant="copy3" color="punch">
                Invitation - {msg}
              </Typography>
            )}
          />
          <ErrorMessage
            name="non_field_errors"
            render={(msg) => (
              <Typography variant="copy3" color="punch">
                {msg}
              </Typography>
            )}
          />
          <ErrorMessage
            name="non_field_errors"
            render={(msg) => (
              <Typography variant="copy3" color="punch">
                {msg}
              </Typography>
            )}
          />
          <Button
            mt="1rem"
            width="100%"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            type="submit"
          >
            {t('forms.recoverpassword.setnewpassword', 'Set new password')}
          </Button>
        </StyledForm>
      )}
    </Formik>
  );
};

export default RecoverPassword;
